import useFetchItem from "../useFetchItem";
import useMutateItem from "../useMutateItem";
import HttpService from "../httpServices";
import { routes } from "../apiroutes";
import { showSuccessAlert, showErrorAlert } from "../../utils/alert";
import { ErrorHandler } from "../errorHandler";
import { isLoggedIn } from "../authService";

const httpService = new HttpService();

// Get current plan information
export const useGetPlanInfo = (options = {}) => {
  const { data, error, isLoading, refetch } = useFetchItem({
    queryKey: ['planInfo'],
    queryFn: () => httpService.getData(routes.getPlanInfo()),
    enabled: options.enabled !== false,
    retry: 2,
    onError: (err) => {
      const error = ErrorHandler(err);
      // Don't show error alert for plan info as it might be expected
      console.warn('Plan info error:', error);
    },
  });

  // Extract the actual plan data from the Axios response
  const planData = data?.data || data;

  return {
    planInfo: planData,
    planInfoError: ErrorHandler(error),
    planInfoIsLoading: isLoading,
    refetchPlanInfo: refetch,
  };
};

// Update plan info (country and phone)
export const useUpdatePlanInfo = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.putData(payload, routes.updatePlanInfo()),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Profile updated successfully");
      handleSuccess?.(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updatePlanInfoData: data,
    updatePlanInfoError: ErrorHandler(error),
    updatePlanInfoIsLoading: isPending,
    updatePlanInfoPayload: (requestPayload) => mutate(requestPayload),
  };
};

// Plan tiers (kept for reference but not used for restrictions)
export const PLAN_TIERS = {
  FREE_TRIAL: 'free_trial',
  STARTER: 'starter_monthly',
  STARTER_ANNUAL: 'starter_annual',
  GROWTH: 'growth_monthly',
  GROWTH_ANNUAL: 'growth_annual',
  ENTERPRISE: 'enterprise',
};

// NOTE: Feature-based restrictions have been removed
// All plans now have access to ALL features
// Only employee limits differentiate plans:
// - Free Trial: 5 employees
// - Starter: 25 employees  
// - Growth: 50 employees
// - Enterprise: Unlimited

// Check if user can add more employees
export const canAddEmployees = (planInfo) => {
  if (!planInfo) return false;
  return planInfo.can_add_employees === true;
};

// NOTE: All feature access functions removed
// All plans now have access to all features
// Feature restrictions are now only role-based (branch users vs main branch users)

// Get plan tier name
export const getPlanName = (planInfo) => {
  if (!planInfo) return 'Unknown';
  return planInfo.plan_name || 'Unknown';
};

// Get employee limit info
export const getEmployeeLimitInfo = (planInfo) => {
  if (!planInfo) return { current: 0, limit: 0, remaining: 0 };
  return {
    current: planInfo.current_employees || 0,
    limit: planInfo.employee_limit || 0,
    remaining: planInfo.remaining_employees || 0,
  };
};

// Check if employee limit is reached
export const isEmployeeLimitReached = (planInfo) => {
  if (!planInfo) return false;
  return !planInfo.can_add_employees;
};

// Get upgrade requirements
export const getUpgradeRequirements = (planInfo) => {
  if (!planInfo || !planInfo.upgrade_required) return {};
  return planInfo.upgrade_required;
};

// Handle API errors for plan restrictions
export const handlePlanRestrictionError = (error) => {
  if (!error) return null;
  
  const errorMessage = error.detail || error.message || 'Unknown error';
  
  // Check for employee limit exceeded
  if (errorMessage.includes('Employee limit exceeded')) {
    return {
      type: 'employee_limit',
      message: errorMessage,
      action: 'upgrade_plan'
    };
  }
  
  // Check for feature access denied
  if (errorMessage.includes('requires') && errorMessage.includes('plan')) {
    return {
      type: 'feature_access',
      message: errorMessage,
      action: 'upgrade_plan'
    };
  }
  
  return {
    type: 'unknown',
    message: errorMessage,
    action: 'contact_support'
  };
};

// Plan restriction hook for components
export const usePlanRestrictions = () => {
  const loggedIn = isLoggedIn();
  const { planInfo, planInfoIsLoading, planInfoError } = useGetPlanInfo({
    enabled: loggedIn === true,
  });

  const restrictions = {
    // Raw plan info for components that need it
    planInfo: planInfo,
    
    // Employee limits (only restriction remaining)
    canAddEmployees: canAddEmployees(planInfo),
    isEmployeeLimitReached: isEmployeeLimitReached(planInfo),
    employeeLimits: getEmployeeLimitInfo(planInfo),
    
    // Plan info
    planName: getPlanName(planInfo),
    upgradeRequirements: getUpgradeRequirements(planInfo),
    
    // Loading and error states
    isLoading: planInfoIsLoading,
    error: planInfoError,
  };

  return restrictions;
}; 